import React, { useEffect, useMemo, useState } from "react";
import { useWallet } from './SignedWalletProvider'; // Adjust the import path

/**
 * PlanUsage.jsx (refactored)
 * - JavaScript (no TypeScript)
 * - TailwindCSS dark theme with cyan & purple accents
 * - Fully responsive: mobile-first layout, adapts content density by viewport
 * - Reactive: auto-updates via optional refresh interval; responsive UI reacts to screen size
 *
 * Props:
 *   apiUrl     (string)  -> default: "http://localhost:8000/usage"
 *   token      (string)  -> Bearer token (required)
 *   refreshMs  (number)  -> optional auto-refresh interval in ms
 *   lowQuotaAt (number)  -> percentage threshold to mark usage as low (default 90)
 *   onUpgrade  (fn)      -> optional handler to trigger upgrade flow
 */
export default function PlanUsage({
  config,
  refreshMs = 360000, // default 60s
  lowQuotaAt = 90,
  onUpgrade,
}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const isMobile = useIsMobile();

  const { jwtToken, network, account } = useWallet();
  const baseUrl = config.apiBaseUrl;
  const apiUrl = `${baseUrl.replace(/\/+$/,"")}/usage`;

  const fetchUsage = async () => {
    if (!jwtToken) {
      setErr("Missing Bearer token");
      setLoading(false);
      return;
    }
    setLoading(true);
    setErr(null);
    try {
      const res = await fetch(apiUrl, {
        headers: {
          accept: "application/json",
          Authorization: `Bearer ${jwtToken}`,
        },
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(`HTTP ${res.status}: ${text || res.statusText}`);
      }
      const json = await res.json();
      setData(json);
    } catch (e) {
      setErr(e.message || "Failed to fetch usage");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsage();
    if (refreshMs && Number.isFinite(refreshMs) && refreshMs > 0) {
      const id = setInterval(fetchUsage, refreshMs);
      return () => clearInterval(id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiUrl, jwtToken, refreshMs]);

  const { used, limit, remaining, pct, plan, sub, lowQuota } = useMemo(() => {
    const used = Number(data?.monthly_api_calls ?? 0);
    const limitRaw = Number(data?.subscription?.plan?.api_calls_per_month);
    const finiteLimit = Number.isFinite(limitRaw) && limitRaw > 0 ? limitRaw : Infinity;
    const pct = Number.isFinite(finiteLimit) && finiteLimit !== Infinity
      ? Math.min(100, Math.max(0, Math.round((used / finiteLimit) * 100)))
      : null;
    const remaining = finiteLimit === Infinity ? Infinity : Math.max(0, finiteLimit - used);
    const lowQuota = pct != null && pct >= lowQuotaAt;
    return {
      used,
      limit: finiteLimit,
      remaining,
      pct,
      lowQuota,
      plan: data?.subscription?.plan || null,
      sub: data?.subscription || null,
    };
  }, [data, lowQuotaAt]);

  // helpers
  const fmtInt = (n) => (n === Infinity ? "∞" : new Intl.NumberFormat().format(Math.max(0, Math.floor(n))));
  const fmtMoney = (cents) => (Number.isFinite(cents) ? `$${(cents / 100).toFixed(2)}` : "—");
  const fmtDate = (s) => {
    if (!s) return "—";
    const d = new Date(s);
    if (isNaN(d.getTime())) return "—";
    return d.toLocaleString(undefined, { year: "numeric", month: "short", day: "2-digit" });
  };

  return (
    <section
      className={[
        "w-full",
        "bg-gradient-to-b from-gray-950 via-gray-950 to-gray-900",
        "text-gray-200 px-4 sm:px-6 md:px-8 py-6 sm:py-10",
        "rounded-2xl border border-white/10",
      ].join(" ")}
    >
      {/* Header */}
      <div className="relative mb-6 sm:mb-8">
        <div className="pointer-events-none absolute inset-x-0 -top-24 h-48 opacity-60 blur-3xl">
          <div className="mx-auto h-full w-3/4 rounded-full bg-gradient-to-r from-cyan-500/30 via-purple-500/30 to-cyan-500/30" />
        </div>
        <div className="relative flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <h2 className="text-2xl sm:text-3xl font-bold text-white mt-0">Plan Usage</h2>
            <p className="mt-2 text-xs sm:text-sm text-gray-300">
              Current month API usage and remaining quota.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <TierBadge plan={plan} />
            <button
              onClick={fetchUsage}
              className="rounded-xl px-3 py-2 text-sm font-semibold bg-white/10 hover:bg-white/15 text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400/60"
            >
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* States */}
      {loading && (
        <div className="flex items-center justify-center py-14 text-gray-300">
          <Spinner />
          <span className="ml-3">Loading usage…</span>
        </div>
      )}
      {!loading && err && (
        <div className="rounded-xl border border-red-500/30 bg-red-500/10 text-red-200 p-4">
          <div className="font-semibold">Error</div>
          <div className="text-sm mt-1 break-words">{String(err)}</div>
        </div>
      )}

      {!loading && !err && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main usage card */}
          <div className="lg:col-span-2">
            <div className="rounded-2xl p-[3px] bg-[conic-gradient(at_top_right,_theme(colors.cyan.500),theme(colors.purple.500),theme(colors.cyan.500))] shadow/40 shadow-black/30">
              <div className="rounded-2xl bg-gray-900/95 p-5 sm:p-8">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div>
                    <h3 className="text-base sm:text-lg font-semibold text-white mt-0">Monthly API Calls</h3>
                    <p className="text-[11px] sm:text-xs text-gray-400">Resets each billing month</p>
                  </div>
                  <div className="text-right">
                    <div className="text-xl sm:text-2xl font-bold text-white">
                      {fmtInt(used)} <span className="text-gray-400 text-sm sm:text-base">/ {fmtInt(limit)}</span>
                    </div>
                    <div className="text-[11px] sm:text-xs text-gray-400">Remaining: <span className="text-white font-medium">{fmtInt(remaining)}</span></div>
                  </div>
                </div>

                {/* Progress bar (responsive) */}
                <div className="mt-5">
                  <div className="h-2 sm:h-3 w-full rounded-full bg-white/10 overflow-hidden">
                    <div
                      className={[
                        "h-full rounded-full",
                        lowQuota
                          ? "bg-gradient-to-r from-purple-500 to-pink-600"
                          : "bg-gradient-to-r from-cyan-500 to-purple-600",
                      ].join(" ")}
                      style={{ width: pct == null ? 0 : `${Math.max(0, Math.min(100, pct))}%` }}
                      role="progressbar"
                      aria-valuenow={pct ?? 0}
                      aria-valuemin={0}
                      aria-valuemax={100}
                    />
                  </div>
                  <div className="mt-2 flex items-center justify-between text-[11px] sm:text-xs text-gray-400">
                    <span>{pct == null ? "Usage percentage not applicable" : `${pct}% used`}</span>
                    {lowQuota && (
                      <span className="text-purple-300">Approaching limit</span>
                    )}
                  </div>
                </div>

                {/* Mobile compact badges */}
                {isMobile && (
                  <div className="mt-4 flex flex-wrap gap-2">
                    <Flag label="Mainnet oracles" enabled={!!plan?.allows_mainnet_oracles} />
                    <Flag label="Priority queue" enabled={!!plan?.priority_queue} />
                    <Flag label="All metrics" enabled={!!plan?.access_all_metrics} />
                  </div>
                )}

                {/* Optional Upgrade CTA when low quota */}
                {lowQuota && onUpgrade && (
                  <div className="mt-5">
                    <button
                      onClick={onUpgrade}
                      className="w-full sm:w-auto rounded-xl px-4 py-2.5 text-sm font-semibold bg-gradient-to-r from-cyan-500 to-purple-600 text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400/60"
                    >
                      Upgrade plan
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Subscription details (denser on desktop, collapses naturally on mobile) */}
          <div className="rounded-2xl p-[1px] bg-white/5 shadow/40 shadow-black/30">
            <div className="rounded-2xl bg-gray-900/95 p-5 sm:p-6 h-full">
              <h3 className="text-base sm:text-lg font-semibold text-white mb-4 mt-0">Subscription</h3>
              <dl className="grid grid-cols-2 gap-3 text-sm">
                <KV label="Tier" value={plan?.tier || "—"} />
                <KV label="Status" value={sub?.status || "—"} />
                <KV label="Period" value={sub?.period || "—"} />
                <KV label="Ends" value={fmtDate(sub?.ends_at)} />
                <KV label="API limit/mo" value={fmtInt(limit)} />
                <KV label="Price (mo)" value={fmtMoney(plan?.price_monthly_cents)} />
                <KV label="Price (yr)" value={fmtMoney(plan?.price_annual_cents)} />
                <KV label="SLA" value={plan?.has_sla ? "Yes" : "No"} />
              </dl>
              {/* Desktop-only flags here; mobile shows inside main card */}
              <div className="mt-4 hidden sm:flex flex-wrap gap-2">
                <Flag label="Mainnet oracles" enabled={!!plan?.allows_mainnet_oracles} />
                <Flag label="Priority queue" enabled={!!plan?.priority_queue} />
                <Flag label="All metrics" enabled={!!plan?.access_all_metrics} />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Mobile sticky summary footer: shows remaining at a glance */}
      {!loading && !err && isMobile && (
        <div className="mt-6 sticky bottom-3 z-10">
          <div className="mx-auto max-w-md rounded-xl border border-white/10 bg-gray-900/95 px-4 py-3 shadow-lg">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-300">Remaining</span>
              <span className="font-semibold text-white">{fmtInt(remaining)}</span>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

/* ---------- Subcomponents ---------- */

function KV({ label, value }) {
  return (
    <div className="rounded-xl bg-white/5 border border-white/10 px-3 py-2">
      <dt className="text-xs text-gray-400">{label}</dt>
      <dd className="mt-0.5 text-white text-sm font-medium break-words">{value}</dd>
    </div>
  );
}

function Flag({ label, enabled }) {
  return (
    <span
      className={[
        "text-[11px] rounded-md px-2 py-1 border",
        enabled
          ? "border-cyan-400/40 text-cyan-300 bg-cyan-400/10"
          : "border-white/10 text-gray-300 bg-white/5",
      ].join(" ")}
    >
      {label}
    </span>
  );
}

function TierBadge({ plan }) {
  if (!plan?.tier) return null;
  const t = String(plan.tier).toUpperCase();
  const base = "text-xs px-2 py-1 rounded-md border";
  const cls = t === "GROWTH"
    ? "border-cyan-400/40 text-cyan-300 bg-cyan-400/10"
    : t === "STARTER"
    ? "border-purple-400/40 text-purple-300 bg-purple-400/10"
    : t === "FREE"
    ? "border-white/10 text-gray-200 bg-white/5"
    : "border-cyan-400/40 text-cyan-200 bg-cyan-400/10";
  return <span className={[base, cls].join(" ")}>{plan.tier}</span>;
}

function Spinner() {
  return (
    <div className="relative inline-flex">
      <div className="h-5 w-5 animate-spin rounded-full border-2 border-cyan-400/70 border-t-transparent" />
    </div>
  );
}

/* ---------- Hooks ---------- */

function useIsMobile(breakpoint = 640) { // Tailwind sm = 640px
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.innerWidth < breakpoint;
  });
  useEffect(() => {
    if (typeof window === "undefined") return;
    const onResize = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [breakpoint]);
  return isMobile;
}
